
import type { StatsResponse } from '@empathybroker/tsclient-stats/dist/models/stats-response'
import type {
  TrendingData,
  TrendingDataPoint,
} from '@empathybroker/tsclient-stats/dist/models/trendingreport/trendingdata'
import type { Bubble, BubblesClusterConfig, Params } from '@empathyco/insights'
import type { PropType } from 'vue'
import type {
  CategoriesTreeItem,
  Dictionary,
  RegularFilterSelectedValues,
  WidgetOptions,
  WidgetOptionsItem,
} from '../../models'
import type { NoDataMessage } from './no-data.component.vue'
import { DomainEnum } from '@empathybroker/tsclient-play/dist/enums/domain.enum'
import { StatsReportsIdsEnum } from '@empathybroker/tsclient-stats/dist/enums/stats-reports-ids.enum'
import { BubblesClusterComponent, BubblesClusterOrientationEnum } from '@empathyco/insights'

import { defineComponent } from 'vue'

import { mapGetters } from 'vuex'

import { useKpis } from '../../composables/use-kpis'

import { FeatureIdEnum, KpiEnum } from '../../enums'

import { InsightsMixin } from '../../mixins'

import { APP_CONSTS } from '../../store/app/app.consts'

import { FeaturesTreeUtils, getKpiTrendingCount, getKpiTrendingData } from '../../utils'
import { EpbText } from '../shared/epb-text'

import Widget from '../widget.component.vue'
import NoData from './no-data.component.vue'

export interface Data {
  trending: TrendingData
}

export default defineComponent({
  name: 'NowWidget',
  components: {
    Widget,
    EpbText,
    NoData,
  },
  mixins: [InsightsMixin],
  props: {
    setup: Object as PropType<{ options: WidgetOptions }>,
    filters: Object as PropType<Record<string, RegularFilterSelectedValues>>,
    category: Object as PropType<CategoriesTreeItem>,
    noData: Object as PropType<Dictionary<string>>,
  },
  setup() {
    const { KpisOfTools, kpiOptions } = useKpis()

    const kpiIds = KpisOfTools[FeatureIdEnum.NOW]

    return { kpiOptions, kpiIds }
  },
  computed: {
    ...mapGetters(APP_CONSTS.moduleName, {
      featuresTree: APP_CONSTS.getters.featuresTree,
    }),
    feature(): FeatureIdEnum {
      const validCategoryFeature = FeaturesTreeUtils.getFeatureNode(
        FeatureIdEnum.NOW,
        this.featuresTree[DomainEnum.SEARCH],
      )

      return validCategoryFeature?.routeName as FeatureIdEnum
    },
    options(): WidgetOptions {
      const options = this.setup?.options
      if (!options) {
        return this.defaultOptions
      }
      const selectedOption = options.items.find((item: WidgetOptionsItem) => item.selected)
      options.items = this.kpiIds.reduce(
        (items: WidgetOptionsItem[], kpi: KpiEnum, index: number, kpis: KpiEnum[]) => {
          const isSelectedOptionInKpis = kpis.includes(selectedOption?.id)
          const isKpiSelected = isSelectedOptionInKpis
            ? selectedOption?.id === kpi
            : index === this.kpiIds.length - 1
          const kpiSelected = this.category ? KpiEnum.CLICKS : kpi
          const optionsItem = this.getOptionsItem(kpiSelected, isKpiSelected)
          items.push(optionsItem)
          return items
        },
        [],
      )
      return options
    },
    defaultOptions(): WidgetOptions {
      const items = this.category
        ? []
        : this.kpiIds.map((kpi: KpiEnum, id: number, kpis: KpiEnum[]) =>
            this.getOptionsItem(kpi, id === kpis.length - 1),
          )
      return {
        items,
        documentationLink: this.messages.link,
      }
    },
    noDataMessage(): NoDataMessage {
      return {
        title: this.noData.title,
        ...(this.filters && { subtitle: this.noData.filters }),
        ...(this.category && { subtitle: this.noData.category }),
      }
    },
    hasDataLoaded(): boolean {
      return !!this.getKpiData(this.selectedOption?.id)?.length
    },
    selectedId(): KpiEnum {
      return this.selectedOption?.id
    },
    selectedLabel(): string {
      return this.selectedOption?.label
    },
    selectedOption(): WidgetOptionsItem {
      if (this.category) {
        return this.kpiIds.includes(KpiEnum.ADD2CART)
          ? (this.getOptionsItem(KpiEnum.ADD2CART, true) ?? undefined)
          : (this.getOptionsItem(KpiEnum.CLICKS, true) ?? undefined)
      }

      return this.options.items.find((option: WidgetOptionsItem) => option.selected)
    },
    componentParams(): Params<BubblesClusterConfig, Bubble[]> {
      const bubblesMaxRadius = 60
      return {
        config: {
          maxRadius: bubblesMaxRadius,
          hoverRadius: bubblesMaxRadius,
          ...(!!this.selectedOption?.label && {
            valueLabel: this.selectedOption.label,
          }),
          orientation: BubblesClusterOrientationEnum.FULL,
          transitionsDurationInMillis: 1200,
          delayTransitionInMillis: 100,
          animated: true,
          showDefaultCursor: true,
        },
        data: this.componentData,
      }
    },
    componentData(): Bubble[] {
      if (!this.data?.trending || !this.selectedOption) {
        return []
      }
      const kpiData = this.getKpiData(this.selectedOption.id)
      const data = kpiData
        .sort(
          (trendingDataPoint1: TrendingDataPoint, trendingDataPoint2: TrendingDataPoint) =>
            trendingDataPoint2.count - trendingDataPoint1.count,
        )
        .slice(0, this.bubblesCount)
      return data.map((trendingDataPoint: TrendingDataPoint) => ({
        name: trendingDataPoint.query,
        value: trendingDataPoint.count,
      }))
    },
  },
  data(): Record<string, any> {
    return {
      baseClass: 'epb-now-widget',
      loadingData: true,
      fetchDataInterval: undefined,
      component: undefined,
      bubblesCount: 5,
      realtimeMinutes: 15,
      messages: this.$epb.messages.highlights.widget.now,
    }
  },
  watch: {
    componentParams: {
      handler(params: Params<BubblesClusterConfig, Bubble[]>): void {
        if (this.$refs.component) {
          this.component.update(params)
        }
      },
      deep: true,
    },
    filters: {
      async handler(
        newFilters: Record<string, RegularFilterSelectedValues>,
        oldFilters: Record<string, RegularFilterSelectedValues>,
      ): Promise<void> {
        if (JSON.stringify(newFilters) !== JSON.stringify(oldFilters)) {
          await this.loadData()
        }
      },
      deep: true,
    },
    category: {
      async handler(
        newCategory: CategoriesTreeItem,
        oldCategory: CategoriesTreeItem,
      ): Promise<void> {
        if (JSON.stringify(newCategory) !== JSON.stringify(oldCategory)) {
          await this.loadData()
        }
      },
      deep: true,
    },
  },
  created(): void {
    this.component = new BubblesClusterComponent()
  },
  mounted(): void {
    this.fetchDataInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        this.updateTrendingData()
      }
    }, 30000)
  },
  beforeDestroy(): void {
    clearInterval(this.fetchDataInterval)

    if (this.hasDataLoaded) {
      this.component.destroy()
    }
  },
  methods: {
    onEnter(): void {
      this.$nextTick(() => {
        this.component.init(this.$refs.component, this.componentParams)
      })
    },
    onLeave(): void {
      this.component.destroy()
    },
    getKpiData(kpi: KpiEnum): TrendingDataPoint[] {
      if (!this.data?.trending) {
        return []
      }
      const trending = this.data.trending
      return getKpiTrendingData(kpi, trending)
    },
    showKpiValue(kpi: KpiEnum): boolean {
      return this.data && kpi && !this.loadingData
    },
    getKpiValue(kpi: KpiEnum): string {
      const numQueries = this.getKpiCount(KpiEnum.QUERIES)
      let kpiValue = numQueries
      if (kpi !== KpiEnum.QUERIES) {
        kpiValue = this.category
          ? this.getKpiCount(kpi)
          : (this.getKpiCount(kpi) / numQueries) * 100
      }
      if (isNaN(kpiValue) || !isFinite(kpiValue)) {
        kpiValue = 0
      }
      const kpiStringValue: string = this.$epb.utils.formatNumber(
        kpiValue,
        { maximumFractionDigits: 2 },
        true,
      )
      return kpi === KpiEnum.QUERIES || this.category ? kpiStringValue : `${kpiStringValue}%`
    },
    getKpiCount(kpi: KpiEnum): number {
      return getKpiTrendingCount(kpi, this.data.trending.totals)
    },
    getOptionsItem(kpi: KpiEnum, selected: boolean): WidgetOptionsItem {
      return {
        ...this.kpiOptions.find(({ id }) => id === kpi),
        selected,
      }
    },
    async fetchData(): Promise<Data> {
      this.loadingData = true
      const trending = await this.getTrendingData()
      this.loadingData = false
      return {
        trending,
      }
    },
    async getTrendingData(): Promise<TrendingDataPoint[]> {
      const trendingResponse: StatsResponse = await this.fetchStatsReport({
        id: StatsReportsIdsEnum.TRENDING,
        params: {
          ...this.getPeriod(),
          ...(this.filters && { filters: this.filters }),
          ...(this.category && {
            categoryId: this.category.id,
            level: this.category.level,
            ...(this.category.level > 1 && { parentId: this.category.parentId }),
          }),
        },
      })

      return trendingResponse.data
    },
    getPeriod(): { startDate: Date; endDate: Date } {
      const endDate = new Date()
      const startDate = new Date(endDate.getTime() - this.realtimeMinutes * 60 * 1000)
      return {
        startDate,
        endDate,
      }
    },
    async updateTrendingData(): Promise<void> {
      this.data.trending = await this.getTrendingData()
    },
  },
})
